import { fetchWithAuth, BASE_URL } from "../api/client";

/**
 * Listings Management Service
 * Handles all API calls for real estate listings management
 * Mirrors the structure of LeadsService.js for consistency
 */

// Valid listing statuses
export const LISTING_STATUSES = {
  ACTIVE: "active",
  PENDING: "pending",
  UNDER_CONTRACT: "under_contract",
  SOLD: "sold",
  EXPIRED: "expired",
  WITHDRAWN: "withdrawn",
};

// Valid stage transitions - defines which statuses can transition to which
export const VALID_TRANSITIONS = {
  active: ["pending", "under_contract", "expired", "withdrawn"],
  pending: ["active", "under_contract", "withdrawn"],
  under_contract: ["active", "pending", "sold", "withdrawn"],
  sold: [], // Terminal state - deal closed
  expired: ["active"], // Can be relisted
  withdrawn: ["active"], // Can be relisted
};

/**
 * Get valid status transitions for a given current status
 * @param {string} currentStatus - The current status of the listing
 * @returns {string[]} Array of valid status values the listing can transition to
 */
export const getValidTransitions = (currentStatus) => {
  const normalized = (currentStatus || "active").toLowerCase();
  return VALID_TRANSITIONS[normalized] || [];
};

// Source display configuration for listing origins
export const SOURCE_CONFIG = {
  mls: {
    label: "MLS",
    color: "blue",
    bgClass: "bg-blue-100 dark:bg-blue-900/30",
    textClass: "text-blue-700 dark:text-blue-400",
  },
  zillow: {
    label: "Zillow",
    color: "indigo",
    bgClass: "bg-indigo-100 dark:bg-indigo-900/30",
    textClass: "text-indigo-700 dark:text-indigo-400",
  },
  realtor: {
    label: "Realtor.com",
    color: "red",
    bgClass: "bg-red-100 dark:bg-red-900/30",
    textClass: "text-red-700 dark:text-red-400",
  },
  fsbo: {
    label: "FSBO",
    color: "amber",
    bgClass: "bg-amber-100 dark:bg-amber-900/30",
    textClass: "text-amber-700 dark:text-amber-400",
  },
  direct: {
    label: "Direct",
    color: "emerald",
    bgClass: "bg-emerald-100 dark:bg-emerald-900/30",
    textClass: "text-emerald-700 dark:text-emerald-400",
  },
  referral: {
    label: "Referral",
    color: "purple",
    bgClass: "bg-purple-100 dark:bg-purple-900/30",
    textClass: "text-purple-700 dark:text-purple-400",
  },
  unknown: {
    label: "Unknown",
    color: "slate",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-600 dark:text-slate-400",
  },
};

// Status display configuration
export const STATUS_CONFIG = {
  active: {
    label: "Active",
    color: "emerald",
    bgClass: "bg-emerald-100 dark:bg-emerald-900/30",
    textClass: "text-emerald-700 dark:text-emerald-400",
    borderClass: "border-emerald-200 dark:border-emerald-800",
  },
  pending: {
    label: "Pending",
    color: "amber",
    bgClass: "bg-amber-100 dark:bg-amber-900/30",
    textClass: "text-amber-700 dark:text-amber-400",
    borderClass: "border-amber-200 dark:border-amber-800",
  },
  under_contract: {
    label: "Under Contract",
    color: "blue",
    bgClass: "bg-blue-100 dark:bg-blue-900/30",
    textClass: "text-blue-700 dark:text-blue-400",
    borderClass: "border-blue-200 dark:border-blue-800",
  },
  sold: {
    label: "Sold",
    color: "violet",
    bgClass: "bg-violet-100 dark:bg-violet-900/30",
    textClass: "text-violet-700 dark:text-violet-400",
    borderClass: "border-violet-200 dark:border-violet-800",
  },
  expired: {
    label: "Expired",
    color: "orange",
    bgClass: "bg-orange-100 dark:bg-orange-900/30",
    textClass: "text-orange-700 dark:text-orange-400",
    borderClass: "border-orange-200 dark:border-orange-800",
  },
  withdrawn: {
    label: "Withdrawn",
    color: "slate",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-600 dark:text-slate-400",
    borderClass: "border-slate-200 dark:border-slate-700",
  },
  archive: {
    label: "Archived",
    color: "slate",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-500 dark:text-slate-500",
    borderClass: "border-slate-200 dark:border-slate-700",
  },
};

// Property type configuration
export const PROPERTY_TYPE_CONFIG = {
  single_family: {
    label: "Single Family",
    icon: "Home",
  },
  condo: {
    label: "Condo",
    icon: "Building2",
  },
  townhouse: {
    label: "Townhouse",
    icon: "Home",
  },
  multi_family: {
    label: "Multi-Family",
    icon: "Building2",
  },
  land: {
    label: "Land",
    icon: "MapPin",
  },
  commercial: {
    label: "Commercial",
    icon: "Building2",
  },
};

// ==================== CORE LISTING CRUD ====================

/**
 * Fetch listings with optional filters
 * @param {Object} filters - Optional filters
 * @param {string} filters.source - Filter by source (e.g., "mls", "zillow")
 * @param {string} filters.status - Filter by status
 * @param {string} filters.assigned_to - Filter by assigned agent
 * @param {string} filters.team_id - Filter by team
 * @param {boolean} filters.include_team - If true, include listings from user's team
 * @param {string} filters.property_type - Filter by property type
 * @param {number} filters.limit - Max results to return
 * @returns {Promise<{listings: Listing[], total_count: number}>}
 */
export const fetchListings = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.source) params.append("source", filters.source);
  if (filters.status) params.append("status", filters.status);
  if (filters.assigned_to) params.append("assigned_to", filters.assigned_to);
  if (filters.team_id) params.append("team_id", filters.team_id);
  if (filters.include_team) params.append("include_team", "true");
  if (filters.property_type) params.append("property_type", filters.property_type);
  if (filters.limit) params.append("limit", filters.limit.toString());

  const queryString = params.toString();
  const url = `${BASE_URL}/api/listings${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch listings");
  }

  return response.json();
};

/**
 * Fetch listing statistics for dashboard cards
 * @param {Object} options - Optional settings
 * @param {boolean} options.include_team - If true, include stats from user's team
 * @returns {Promise<{active: number, pending: number, under_contract: number, sold: number, expired: number, withdrawn: number, total: number, total_value: number, avg_days_on_market: number}>}
 */
export const fetchListingStats = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.include_team) params.append("include_team", "true");

  const queryString = params.toString();
  const url = `${BASE_URL}/api/listings/stats${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch listing stats");
  }

  return response.json();
};

/**
 * Fetch a single listing by ID
 * @param {string} listingId
 * @returns {Promise<Listing>}
 */
export const fetchListing = async (listingId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Listing not found");
    }
    throw new Error("Failed to fetch listing");
  }

  return response.json();
};

/**
 * Create a new listing
 * @param {Object} listingData - The listing data
 * @returns {Promise<Listing>}
 */
export const createListing = async (listingData) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(listingData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to create listing");
  }

  return response.json();
};

/**
 * Update a listing
 * @param {string} listingId
 * @param {Object} listingData - Partial listing data
 * @returns {Promise<Listing>}
 */
export const updateListing = async (listingId, listingData) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(listingData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to update listing");
  }

  return response.json();
};

/**
 * Delete a listing (soft delete)
 * @param {string} listingId
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deleteListing = async (listingId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to delete listing");
  }

  return response.json();
};

// ==================== STATUS MANAGEMENT ====================

/**
 * Update listing status
 * @param {string} listingId
 * @param {string} status - One of: active, pending, under_contract, sold, expired, withdrawn
 * @param {string} note - Optional note about the status change
 * @returns {Promise<Listing>}
 */
export const updateListingStatus = async (listingId, status, note = "") => {
  const url = `${BASE_URL}/api/listings/${listingId}/status`;
  // Backend expects uppercase status values
  const apiStatus = status.toUpperCase();
  const body = { status: apiStatus, note };

  console.log('[updateListingStatus] Request:', { url, listingId, status, apiStatus, note, body });

  const response = await fetchWithAuth(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('[updateListingStatus] Error response:', { status: response.status, errorData });
    throw new Error(errorData.detail || errorData.message || "Failed to update listing status");
  }

  return response.json();
};

// ==================== AGENT ASSIGNMENT ====================

/**
 * Assign listing to an agent
 * @param {string} listingId
 * @param {string} assignedTo - Agent email
 * @param {string} role - Role: listing_agent, buyers_agent, or co_listing
 * @param {boolean} sendNotification - Whether to send email notification
 * @returns {Promise<Listing>}
 */
export const assignListing = async (listingId, assignedTo, role = "listing_agent", sendNotification = false) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/assign`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      assigned_to: assignedTo,
      role,
      send_notification: sendNotification
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to assign listing");
  }

  return response.json();
};

/**
 * Get all agents on a listing
 * @param {string} listingId
 * @returns {Promise<{agents: Agent[]}>}
 */
export const fetchListingAgents = async (listingId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/agents`, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch listing agents");
  }

  return response.json();
};

// ==================== NOTES & COMMUNICATION ====================

/**
 * Add a note to a listing
 * @param {string} listingId
 * @param {string} note
 * @param {Array<{email: string, name: string}>} mentionedUsers - Users mentioned in the note
 * @param {string} noteType - Type: general, showing_feedback, offer, price_change, inspection
 * @returns {Promise<Listing>}
 */
export const addListingNote = async (listingId, note, mentionedUsers = [], noteType = "general") => {
  // Validate note is not empty
  const trimmedNote = note?.trim();
  if (!trimmedNote) {
    throw new Error("Note cannot be empty");
  }

  // Check that note has actual content beyond just @mentions
  const contentWithoutMentions = trimmedNote.replace(/@\w+(\.\w+)*(@[\w.]+)?/g, '').trim();
  if (!contentWithoutMentions && mentionedUsers.length > 0) {
    throw new Error("Please add a message along with the mention");
  }

  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/notes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      note: trimmedNote,
      note_type: noteType,
      mentioned_users: mentionedUsers.length > 0
        ? mentionedUsers.map(u => ({ email: u.email, name: u.name || null }))
        : null,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to add note");
  }

  return response.json();
};

/**
 * Update an existing note on a listing
 * @param {string} listingId
 * @param {number} noteIndex - 0-based index of the note to update
 * @param {string} note - New note content
 * @returns {Promise<Listing>}
 */
export const updateListingNote = async (listingId, noteIndex, note) => {
  const trimmedNote = note?.trim();
  if (!trimmedNote) {
    throw new Error("Note cannot be empty");
  }

  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/notes/${noteIndex}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ note: trimmedNote }),
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error("You can only edit your own notes");
    }
    throw new Error("Failed to update note");
  }

  return response.json();
};

/**
 * Delete a note from a listing
 * @param {string} listingId
 * @param {number} noteIndex - 0-based index of the note to delete
 * @returns {Promise<Listing>}
 */
export const deleteListingNote = async (listingId, noteIndex) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/notes/${noteIndex}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error("You can only delete your own notes");
    }
    throw new Error("Failed to delete note");
  }

  return response.json();
};

/**
 * Toggle a reaction (emoji) on a listing note
 * @param {string} listingId
 * @param {number} noteIndex - 0-based index of the note
 * @param {string} emoji - The emoji to toggle
 * @returns {Promise<Listing>}
 */
export const toggleNoteReaction = async (listingId, noteIndex, emoji) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/notes/${noteIndex}/reactions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ emoji }),
  });

  if (!response.ok) {
    throw new Error("Failed to toggle reaction");
  }

  return response.json();
};

// ==================== SHOWINGS MANAGEMENT ====================

/**
 * Fetch all showings for a listing
 * @param {string} listingId
 * @returns {Promise<{showings: Showing[]}>}
 */
export const fetchListingShowings = async (listingId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/showings`, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch showings");
  }

  return response.json();
};

/**
 * Schedule a showing
 * @param {string} listingId
 * @param {Object} showingData - Showing details
 * @returns {Promise<Showing>}
 */
export const createShowing = async (listingId, showingData) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/showings`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(showingData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to schedule showing");
  }

  return response.json();
};

/**
 * Update a showing
 * @param {string} listingId
 * @param {string} showingId
 * @param {Object} showingData - Partial showing data
 * @returns {Promise<Showing>}
 */
export const updateShowing = async (listingId, showingId, showingData) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/showings/${showingId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(showingData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to update showing");
  }

  return response.json();
};

// ==================== OFFERS MANAGEMENT ====================

/**
 * Fetch all offers for a listing
 * @param {string} listingId
 * @returns {Promise<{offers: Offer[]}>}
 */
export const fetchListingOffers = async (listingId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/offers`, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch offers");
  }

  return response.json();
};

/**
 * Record a new offer
 * @param {string} listingId
 * @param {Object} offerData - Offer details
 * @returns {Promise<Offer>}
 */
export const createOffer = async (listingId, offerData) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/offers`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(offerData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to record offer");
  }

  return response.json();
};

/**
 * Update an offer
 * @param {string} listingId
 * @param {string} offerId
 * @param {Object} offerData - Partial offer data
 * @returns {Promise<Offer>}
 */
export const updateOffer = async (listingId, offerId, offerData) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/offers/${offerId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(offerData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to update offer");
  }

  return response.json();
};

// ==================== CONVERSION (SOLD) ====================

/**
 * Mark listing as sold/closed
 * @param {string} listingId
 * @param {Object} closeData - Closing details
 * @returns {Promise<{success: boolean, message: string, listing_id: string, transaction_id: string, transaction: Object}>}
 */
export const closeListing = async (listingId, closeData = {}) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/close`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(closeData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to close listing");
  }

  return response.json();
};

// ==================== ARCHIVE & RESTORE ====================

/**
 * Fetch archived listings
 * @param {Object} options - Query options
 * @param {boolean} options.include_team - Include team members' archived listings
 * @returns {Promise<{listings: Listing[], total_count: number}>}
 */
export const fetchArchivedListings = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.include_team) params.append("include_team", "true");

  const url = `${BASE_URL}/api/listings/archived${params.toString() ? `?${params.toString()}` : ""}`;
  const response = await fetchWithAuth(url, {
    headers: { accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch archived listings");
  }

  return response.json();
};

/**
 * Archive a listing
 * @param {string} listingId - The listing ID to archive
 * @param {string} reason - Reason for archiving
 * @returns {Promise<{success: boolean, message: string, listing_id: string}>}
 */
export const archiveListing = async (listingId, reason = "") => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/archive`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 404) {
      throw new Error("Listing not found or you don't have access to it");
    }
    if (response.status === 400) {
      throw new Error(errorData.detail || "Listing is already archived");
    }
    throw new Error(errorData.detail || "Failed to archive listing");
  }

  return response.json();
};

/**
 * Restore an archived listing
 * @param {string} listingId - The listing ID to restore
 * @returns {Promise<{success: boolean, message: string, listing_id: string}>}
 */
export const restoreListing = async (listingId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/restore`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 404) {
      throw new Error("Listing not found or you don't have access to it");
    }
    if (response.status === 400) {
      throw new Error(errorData.detail || "Listing is not archived");
    }
    throw new Error(errorData.detail || "Failed to restore listing");
  }

  return response.json();
};

// Common archive reasons for UI dropdown
export const ARCHIVE_REASONS = [
  "Expired",
  "Withdrawn by seller",
  "Seller changed mind",
  "Listed with another agent",
  "Property sold off-market",
  "Duplicate listing",
  "Other",
];

// ==================== BUYER LEADS (MARKETPLACE) ====================

/**
 * Fetch buyer leads pool (preview only, no PII)
 * @param {Object} filters - Optional filters
 * @returns {Promise<{leads: BuyerLeadPreview[], total_count: number}>}
 */
export const fetchBuyerLeadsPool = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.property_type) params.append("property_type", filters.property_type);
  if (filters.min_budget) params.append("min_budget", filters.min_budget.toString());
  if (filters.max_budget) params.append("max_budget", filters.max_budget.toString());
  if (filters.location) params.append("location", filters.location);
  if (filters.pre_approved) params.append("pre_approved", "true");
  if (filters.limit) params.append("limit", filters.limit.toString());

  const queryString = params.toString();
  const url = `${BASE_URL}/api/listings/buyer-leads${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch buyer leads");
  }

  return response.json();
};

/**
 * Unlock a buyer lead with credits
 * @param {string} leadId - The lead ID to unlock
 * @param {string} accessCode - The 8-character access code
 * @returns {Promise<{success: boolean, lead: BuyerLead, credits_charged: number}>}
 */
export const unlockBuyerLead = async (leadId, accessCode) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/buyer-leads/${leadId}/unlock`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ access_code: accessCode }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 400) {
      throw new Error(errorData.detail || "Invalid or expired access code");
    }
    if (response.status === 402) {
      throw new Error(errorData.detail || "Insufficient credits to unlock this lead");
    }
    if (response.status === 409) {
      throw new Error(errorData.detail || "You have already unlocked this lead");
    }
    throw new Error(errorData.detail || "Failed to unlock lead");
  }

  return response.json();
};

// ==================== SHARING & REFERRALS ====================

/**
 * Share listing with mortgage lender
 * @param {string} listingId
 * @param {Object} data - Lender and buyer information
 * @returns {Promise<Object>}
 */
export const shareWithLender = async (listingId, data) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/share-with-lender`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 403) {
      throw new Error(errorData.message || "This feature is only available for Ultra and Enterprise plans");
    }
    throw new Error(errorData.message || "Failed to share listing with lender");
  }

  return response.json();
};

/**
 * Share/refer listing to another agent
 * @param {string} listingId
 * @param {Object} data - Agent and referral information
 * @returns {Promise<Object>}
 */
export const shareWithAgent = async (listingId, data) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/share-with-agent`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 403) {
      throw new Error(errorData.message || "This feature is only available for Ultra and Enterprise plans");
    }
    throw new Error(errorData.message || "Failed to share listing with agent");
  }

  return response.json();
};

// ==================== PRICE HISTORY ====================

/**
 * Record a price change
 * @param {string} listingId
 * @param {Object} data - Price change details
 * @returns {Promise<Listing>}
 */
export const recordPriceChange = async (listingId, data) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/price-change`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to record price change");
  }

  return response.json();
};

/**
 * Get price change history
 * @param {string} listingId
 * @returns {Promise<{history: PriceChange[]}>}
 */
export const fetchPriceHistory = async (listingId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/${listingId}/price-history`, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch price history");
  }

  return response.json();
};

// ==================== TEAM CONTEXT ====================

/**
 * Fetch the team context for the current user
 * @returns {Promise<{success: boolean, data: {team_owner_id: string, is_owner: boolean, user_email: string, team_members: Array}}>}
 */
export const fetchTeamContext = async () => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/team-context`, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch team context");
  }

  return response.json();
};

// ==================== DAILY REPORTS ====================

/**
 * Fetch list of available daily reports
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of reports to fetch (1-90, default 30)
 * @returns {Promise<{success: boolean, data: {team_id: string, reports: Array, total_count: number}}>}
 */
export const fetchListingsReports = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.limit) params.append("limit", options.limit.toString());

  const queryString = params.toString();
  const url = `${BASE_URL}/api/listings-reports${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch listings reports");
  }

  return response.json();
};

/**
 * Fetch a specific daily report by date
 * @param {string} reportDate - Date in YYYY-MM-DD format
 * @returns {Promise<{success: boolean, data: Object}>}
 */
export const fetchListingsReport = async (reportDate) => {
  const url = `${BASE_URL}/api/listings-reports/${reportDate}`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Report not found for this date");
    }
    if (response.status === 400) {
      throw new Error("Invalid date format");
    }
    throw new Error("Failed to fetch listings report");
  }

  return response.json();
};

/**
 * Fetch the latest daily report
 * @returns {Promise<{success: boolean, data: Object}>}
 */
export const fetchLatestListingsReport = async () => {
  const url = `${BASE_URL}/api/listings-reports/latest`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("No reports available");
    }
    throw new Error("Failed to fetch latest listings report");
  }

  return response.json();
};

/**
 * Generate a new daily report
 * @param {boolean} force - If true, force regeneration even if report exists for today
 * @returns {Promise<{success: boolean, data: Object, message: string}>}
 */
export const generateListingsReport = async (force = false) => {
  const url = `${BASE_URL}/api/listings-reports/generate?force=${force}`;

  const response = await fetchWithAuth(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 400) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || "Report already exists for today. Use force=true to regenerate.");
    }
    if (response.status === 402) {
      throw new Error("Insufficient credits to generate report");
    }
    throw new Error("Failed to generate listings report");
  }

  return response.json();
};

// ==================== MARKETPLACE ACTIVITY / REPORTS ====================

/**
 * Fetch marketplace activity statistics
 * @returns {Promise<Object>}
 */
export const fetchMarketplaceActivity = async () => {
  const response = await fetchWithAuth(`${BASE_URL}/api/listings/marketplace/activity`, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch marketplace activity");
  }

  return response.json();
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Format listing date for display
 * @param {string} dateStr - ISO timestamp
 * @returns {string}
 */
export const formatListingDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined
  });
};

/**
 * Format phone number for display
 * @param {string} phone
 * @returns {string}
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

/**
 * Format currency for display
 * @param {string|number} value
 * @returns {string}
 */
export const formatCurrency = (value) => {
  if (!value) return "";
  const num = typeof value === "string" ? parseFloat(value.replace(/[^0-9.-]/g, "")) : value;
  if (isNaN(num)) return value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

/**
 * Format currency in abbreviated form (e.g., $93.1M, $398K)
 * @param {number} value
 * @returns {string}
 */
export const formatCurrencyAbbreviated = (value) => {
  if (!value || value === 0) return "$0";

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (absValue >= 1000000000) {
    return `${sign}$${(absValue / 1000000000).toFixed(1)}B`;
  }
  if (absValue >= 1000000) {
    return `${sign}$${(absValue / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    return `${sign}$${(absValue / 1000).toFixed(0)}K`;
  }
  return `${sign}$${absValue.toFixed(0)}`;
};

/**
 * Format percentage with + sign for positive values
 * @param {number} value
 * @returns {string}
 */
export const formatGrowthPercent = (value) => {
  if (value === null || value === undefined) return "";
  const formatted = Math.abs(value).toFixed(0);
  if (value > 0) return `+${formatted}%`;
  if (value < 0) return `-${formatted}%`;
  return "0%";
};

/**
 * Calculate days on market
 * @param {string} listingDate - ISO timestamp of listing date
 * @returns {number}
 */
export const calculateDaysOnMarket = (listingDate) => {
  if (!listingDate) return 0;
  const listing = new Date(listingDate);
  const now = new Date();
  const diffMs = now - listing;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

// Property type options
export const PROPERTY_TYPE_OPTIONS = [
  "Single Family",
  "Condo",
  "Townhouse",
  "Multi-Family (2-4 units)",
  "Land",
  "Commercial",
];

// Financing type options
export const FINANCING_TYPE_OPTIONS = [
  "Cash",
  "Conventional",
  "FHA",
  "VA",
  "USDA",
];

// Contingency options
export const CONTINGENCY_OPTIONS = [
  "Inspection",
  "Financing",
  "Appraisal",
  "Sale of Home",
  "Title",
  "HOA Review",
];

// Price change reason options
export const PRICE_CHANGE_REASONS = [
  "Market adjustment",
  "Seller motivated",
  "Comparable sales",
  "Appraisal",
  "Days on market",
  "Other",
];

// Showing status options
export const SHOWING_STATUS_OPTIONS = [
  "scheduled",
  "completed",
  "cancelled",
  "no_show",
];

// Interest level options
export const INTEREST_LEVEL_OPTIONS = [
  "high",
  "medium",
  "low",
  "none",
];

// Offer status options
export const OFFER_STATUS_OPTIONS = [
  "pending",
  "accepted",
  "rejected",
  "countered",
  "withdrawn",
  "expired",
];

// Agent role options
export const AGENT_ROLE_OPTIONS = [
  "listing_agent",
  "buyers_agent",
  "co_listing",
];
